import {
    debug,
    warn,
    error
} from './logger';

/**
 * General catch-all error code slug
 * @type {String}
 */
export const GENERAL_ERROR = 'GENERAL_ERROR';

/**
 * Tests if a dynamic form should activate parsley for the first time
 *
 * That means:
 *   1 - element exists
 *   2 - parsley has not yet been activated
 *
 * @returns {Boolean} if Parsley should be activated
 */
export function shouldActivateParsley(selector) {
    let element;

    if (_.isString(selector)) {
        element = $(selector);
    } else {
        element = selector;
    }

    const elem = element;

    if (elem.length === 0) {
        return false;
    }

    if (elem.parsley().$element) {
        debug(elem.parsley().$element.selector);

        // This is a weird 'merging' bug with vdom.
        // On the login modal, the #login-form merges with #create-form
        //  when switching screens.  The Parsley validator sticks around.
        //  However, it still has the old selector on it.
        if (elem.parsley().$element.selector &&
            elem.parsley().$element.selector !== selector) {
            elem.parsley().$element.selector = selector;
            elem.parsley()._listeners = [];
            return true;
        }

        if (_.keys(elem.parsley()._listeners).length > 0) {
            // Parsley has been activated
            return false;
        }

        return true;
    }

    return true;
}

/**
 * Check a form for errors. To be used on a form/field event (ex: focusout)
 *
 * @param formId {String} id of the form (including hash) or css class
 * @returns [values, msgs, dateFormats] Values of the form, map of the field errors, moment date formats
 *                            (from the data-error-code attribute)
 *
 * TODO: Upgrade for other input types
 */
export function checkForm(formId) {
    let dateFormats = {};

    $(`${formId} input.js-date-picker`).each((index, elem) => {
        dateFormats[elem.name] = $(elem).attr('data-date-format');
    });

    let values = {};

    $(`${formId} :input[type!='password']`).each(function() {
        const elem = $(this);

        values[this.name] = elem.val();
    });

    let msgs = {};

    $(`${formId} :input`).each(function() {
        const elem = $(this);
        const parsleyElem = elem.parsley();

        // _failedOnce means the field has already been validated false once
        // This prevents errors from popup up on clean/non-dirty fields
        if (parsleyElem._failedOnce && !parsleyElem.isValid()) {
            let failedValidations = [];
            let errMessages;

            for (let prop in this.validity) {
                if (this.validity[prop]) {
                    failedValidations.push(prop);
                }
            }

            if (failedValidations.indexOf('patternMismatch') > -1) {
                // failed on a regex match, return error message appropriately
                errMessages = [elem.data('parsley-pattern-message')];
            } else if (failedValidations.indexOf('tooShort') > -1) {
                // failed on a regex match, return error message appropriately
                errMessages = [elem.data('parsley-minlength-message')];
            }
            else if ($(this).attr('type') === 'email' && failedValidations.indexOf('typeMismatch') > -1) {
                // failed on invalid email address
                let emailErrMessage = elem.data('parsley-email-message') || elem.data('parsley-error-message');

                if (emailErrMessage) {
                    errMessages = [emailErrMessage];
                } else {
                    errMessages = [];
                }
            } else {
                const globalErrMsg = elem.data('parsley-error-message');
                const requiredErrMsg = elem.data('parsley-required-message');
                const dateHasFormatErrMsg = elem.data('parsley-date-has-format-message');
                const equalToErrMsg = elem.data('parsley-equalto-message');

                errMessages = [_.find([globalErrMsg, requiredErrMsg, dateHasFormatErrMsg, equalToErrMsg], (err) => (err))];
            }

            msgs[elem.attr('name')] = errMessages;
        }
    });

    return [values, msgs, dateFormats];
}

/**
 * Simple helper to validate a form with Parsley
 * @param {Object} el jQuery object representing the form element
 * @return {Boolean} false by default, otherwise delegate to Parsley.isValid()
 */
export function isFormValid(el) {
    let isValid = false;

    if (el && el.length > 0) {
        const formParsley = el.parsley();

        formParsley.whenValidate()
            .then(() => formParsley.isValid())
            .then((res) => {
                isValid = res;
            });
    }

    return isValid;
}

/**
 * Map an error code from a service to a Contentful / localized error message
 *
 * @param prefix {String} Error prefix to append to the error code
 * @param error {String} Error code or {Object} with code and message
 * @param error.code  {String} Error code
 */
export function mapErrorCode(prefix, error) {
    if (!error) {
        return GENERAL_ERROR;
    }

    if (error.code) {
        // Expected case
        return `${prefix}_${error.code}`;
    }

    return error;
}

/**
 * Create error compatible with return values of buildErrorsFromMsgs and buildErrorsFromResponse for handling runtime-only GENERAL_ERROR
 * @return {object} error object with GENERAL_ERROR generalError
 */
export function createGenericUnknownError() {
    return {
        generalErrors: ['GENERAL_ERROR']
    };
}

/**
 * Build an error payload from a service response
 *
 * Expects error to be in this format (commonly error.entity)
 *
    {"message": "Validation error", "code": "VALIDATION_ERROR",
    "field_errors": [
        {
            "field": "password",
            "message": "Must be at least 8 characters",
            "code": "MIN_LENGTH_VALIDATION_ERROR"
        },
        {
            "field": "",
            "message": "Password and confirmation password do not match",
            "code": "MISMATCH_PASSWORDS"
        }
    ]
    }
*/
export function buildErrorsFromResponse(response, errorPrefixOverride = '') {
    if (!_.isObject(response)) {
        return createGenericUnknownError();
    }

    let payload = {};
    let errors = response.entity || response.body;
    const errorPrefix = errorPrefixOverride || _.get(response, 'request.errorPrefix', '');

    const generalErrors = [mapErrorCode(errorPrefix, errors)];
    if (errors && errors.field_errors) {
        for (let error of errors.field_errors) {
            if (error.field) {
                if (!payload[error.field]) {
                    payload[error.field] = [];
                }
                payload[error.field].push(mapErrorCode(errorPrefix, error));
            } else {
                generalErrors.push(mapErrorCode(errorPrefix, error));
            }
        }
    }

    payload.generalErrors = generalErrors;
    return payload;
}

/**
 * Build an error payload from a set of form validation errors
 *
 * These should already include the error prefix
 */
export function buildErrorsFromMsgs(msgs) {
    let payload = {};

    if (!msgs) {
        return payload;
    }

    /* Forms should have the prefixes build into the template, thus no prefix should be passed
     *
    * For instance, validatedField("LOGIN_INVALID_USERNAME")
     */
    const errorPrefix = '';

    for (let field in msgs) {
        if (Object.prototype.hasOwnProperty.call(msgs, field)) {
            const errorCode = msgs[field];

            warn(`field: ${field} errorCode: ${errorCode}`);
            if (!payload[field]) {
                payload[field] = [];
            }
            payload[field].push(mapErrorCode(errorPrefix, errorCode));
        }
    }
    return payload;
}
